import type {
	MailAppPassword,
	MailAttachment,
	MailBulk,
	MailCompose,
	MailContact,
	MailFolder,
	MailListResponse,
	MailMessage,
	MailMove,
	MailRule,
	MailStatus,
	MailStorage,
	MailVacation
} from '@andresot/protocol/mail';
import {
	ApiError,
	authorizedFetch,
	authorizedUpload,
	getAuthorizationGeneration,
	assertAuthorizationGeneration,
	request
} from './client.js';

export function fetchMailStatus(): Promise<MailStatus> {
	return request('/api/mail/status');
}

export async function fetchMailFolders(): Promise<MailFolder[]> {
	return (await request<{ folders: MailFolder[] }>('/api/mail/folders')).folders;
}

export function fetchMailMessages(input: {
	mailboxId?: string;
	text?: string;
	position?: number;
	limit?: number;
}): Promise<MailListResponse> {
	const query = new URLSearchParams();
	if (input.mailboxId) query.set('mailboxId', input.mailboxId);
	if (input.text) query.set('text', input.text);
	query.set('position', String(input.position ?? 0));
	query.set('limit', String(input.limit ?? 50));
	return request(`/api/mail/messages?${query}`);
}

export async function fetchMailMessage(id: string): Promise<MailMessage> {
	return (await request<{ message: MailMessage }>(`/api/mail/messages/${encodeURIComponent(id)}`))
		.message;
}

/** Вся переписка целиком — от старых писем к новым. */
export async function fetchMailThread(threadId: string): Promise<MailMessage[]> {
	return (
		await request<{ messages: MailMessage[] }>(`/api/mail/threads/${encodeURIComponent(threadId)}`)
	).messages;
}

export async function fetchMailRules(): Promise<MailRule[]> {
	return (await request<{ rules: MailRule[] }>('/api/mail/rules')).rules;
}

export async function saveMailRules(rules: MailRule[]): Promise<MailRule[]> {
	return (
		await request<{ rules: MailRule[] }>('/api/mail/rules', { method: 'PUT', body: { rules } })
	).rules;
}

/**
 * Забрать картинку и отдать её адресом `blob:`.
 *
 * Тег `<img>` заголовок с токеном отправить не может, а печенье для файлов
 * привязано к своему разделу. Поэтому картинку берём обычным запросом со своим
 * токеном и подставляем уже готовые байты — заодно это единственный путь,
 * который проходит правило источников: `blob:` в нём разрешён, чужие домены нет.
 */
async function objectUrl(url: string): Promise<string> {
	const response = await authorizedFetch(url, { method: 'GET' });
	if (!response.ok) throw new ApiError('Картинка не загрузилась', response.status);
	return URL.createObjectURL(await response.blob());
}

/** Картинка из письма с чужого домена — через наш сервер, по просьбе человека. */
export function fetchRemoteMailImage(source: string): Promise<string> {
	return objectUrl(`/api/mail/remote-image?url=${encodeURIComponent(source)}`);
}

/** Вложение письма как картинка на экране, а не файл на диске. */
export function fetchMailImageAttachment(attachment: MailAttachment): Promise<string> {
	const query = new URLSearchParams({ name: attachment.name, type: attachment.type });
	return objectUrl(`/api/mail/blobs/${encodeURIComponent(attachment.blobId)}?${query}`);
}

export function saveMailDraft(input: MailCompose): Promise<{ message: MailMessage }> {
	return request('/api/mail/drafts', { method: 'POST', body: input });
}

export function sendMail(input: MailCompose): Promise<{ id: string }> {
	return request('/api/mail/send', { method: 'POST', body: input });
}

export function updateMailMessage(id: string, patch: MailMove): Promise<{ updated: true }> {
	return request(`/api/mail/messages/${encodeURIComponent(id)}`, { method: 'PATCH', body: patch });
}

export function deleteMailMessage(id: string): Promise<{ permanent: boolean }> {
	return request(`/api/mail/messages/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function bulkMailMessages(input: MailBulk): Promise<{ changed: number }> {
	return request('/api/mail/messages/bulk', { method: 'POST', body: input });
}

export async function fetchMailStorage(): Promise<MailStorage> {
	return (await request<{ storage: MailStorage }>('/api/mail/storage')).storage;
}

/** Адреса, которым этот человек уже писал: подсказки к полю «Кому». */
export async function fetchMailContacts(query: string, limit = 8): Promise<MailContact[]> {
	const params = new URLSearchParams({ limit: String(limit) });
	if (query) params.set('q', query);
	return (await request<{ contacts: MailContact[] }>(`/api/mail/contacts?${params}`)).contacts;
}

export function deleteMailContact(id: string): Promise<{ deleted: true }> {
	return request(`/api/mail/contacts/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function saveMailSignature(signature: string): Promise<{ signature: string }> {
	return request('/api/mail/signature', { method: 'PUT', body: { signature } });
}

export async function fetchMailVacation(): Promise<MailVacation> {
	return (await request<{ vacation: MailVacation }>('/api/mail/vacation')).vacation;
}

export async function saveMailVacation(input: MailVacation): Promise<MailVacation> {
	return (
		await request<{ vacation: MailVacation }>('/api/mail/vacation', { method: 'PUT', body: input })
	).vacation;
}

export async function fetchMailAppPasswords(): Promise<MailAppPassword[]> {
	return (await request<{ passwords: MailAppPassword[] }>('/api/mail/app-passwords')).passwords;
}

export function createMailAppPassword(
	description: string
): Promise<{ password: MailAppPassword; secret: string }> {
	return request('/api/mail/app-passwords', { method: 'POST', body: { description } });
}

export function deleteMailAppPassword(id: string): Promise<{ deleted: true }> {
	return request(`/api/mail/app-passwords/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export interface MailUpload {
	promise: Promise<MailAttachment>;
	cancel: () => void;
}

/** Загрузка идёт прямо в Stalwart; прогресс берётся из браузерного upload-события. */
export function uploadMailAttachment(file: File, onProgress?: (ratio: number) => void): MailUpload {
	const controller = new AbortController();
	const generation = getAuthorizationGeneration();
	const promise = (async () => {
		const response = await authorizedUpload('/api/mail/attachments', {
			method: 'POST',
			headers: {
				'content-type': 'application/octet-stream',
				// Русское имя кодируется: HTTP-заголовки принимают только латиницу.
				'x-file-name': encodeURIComponent(file.name || 'attachment'),
				'x-file-type': file.type || 'application/octet-stream'
			},
			body: file,
			signal: controller.signal,
			onProgress: (sent) => onProgress?.(file.size ? sent / file.size : 0)
		});
		const payload = (await response.json().catch(() => ({}))) as {
			attachment?: MailAttachment;
			error?: string;
		};
		assertAuthorizationGeneration(generation);
		if (!response.ok || !payload.attachment)
			throw new ApiError(payload.error ?? 'Не удалось загрузить вложение', response.status);
		onProgress?.(1);
		return payload.attachment;
	})();

	return {
		promise,
		cancel: () => controller.abort()
	};
}

export async function downloadMailAttachment(attachment: MailAttachment): Promise<void> {
	const query = new URLSearchParams({ name: attachment.name, type: attachment.type });
	const response = await authorizedFetch(
		`/api/mail/blobs/${encodeURIComponent(attachment.blobId)}?${query}`,
		{ method: 'GET' }
	);
	if (!response.ok) throw new ApiError('Не удалось скачать вложение', response.status);
	const blob = await response.blob();
	const url = URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.download = attachment.name;
	link.hidden = true;
	document.body.append(link);
	link.click();
	link.remove();
	setTimeout(() => URL.revokeObjectURL(url), 30_000);
}
